import { useEffect, useState } from "react";
import { api, formatINR, downloadXlsx } from "@/lib/api";
import { Loader2, Smartphone, Laptop, Monitor, Phone, Car, Tablet, Plus, X, FileSpreadsheet } from "lucide-react";

const ICONS = { Mobile: Smartphone, Laptop: Laptop, Desktop: Monitor, "SIM Card": Phone, Vehicle: Car, Tablet: Tablet };
const TYPES = ["Mobile", "Laptop", "Desktop", "SIM Card", "Vehicle", "Tablet"];

export default function Assets() {
  const [items, setItems] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [type, setType] = useState("");
  const [show, setShow] = useState(false);

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/assets", { params: type ? { asset_type: type } : {} });
    setItems(data);
    setLoading(false);
  };

  useEffect(() => { api.get("/employees").then((r) => setEmployees(r.data)); }, []);
  useEffect(() => { load(); }, [type]);

  return (
    <div className="space-y-6" data-testid="assets-page">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="kpi-label" style={{ color: "var(--color-secondary)" }}>Inventory</div>
          <h1 className="font-heading text-3xl font-bold tracking-tight mt-1">Mobile & Asset Register</h1>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => downloadXlsx("/assets/export.xlsx", "assets_register.xlsx",
              type ? { asset_type: type } : undefined)}
            className="flex items-center gap-2 px-3 py-2 rounded-md text-white text-sm font-medium"
            style={{ background: "#1B4332" }} data-testid="export-assets-xlsx">
            <FileSpreadsheet className="w-4 h-4" /> Excel
          </button>
          <button onClick={() => setShow(true)} data-testid="add-asset-button"
                  className="flex items-center gap-2 px-4 py-2 rounded-md text-white font-medium" style={{ background: "var(--color-primary)" }}>
            <Plus className="w-4 h-4" /> Add Asset
          </button>
        </div>
      </header>

      <div className="flex gap-2 flex-wrap">
        {["", ...TYPES].map((t) => (
          <button key={t || "all"} onClick={() => setType(t)} data-testid={`asset-type-${t || "all"}`}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium transition ${type === t ? "bg-primary text-white" : "bg-white border border-stone-200 text-stone-700 hover:border-primary/40"}`}>
            {t || "All"}
          </button>
        ))}
      </div>

      {loading ? <div className="flex items-center justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div> : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="assets-grid">
          {items.length === 0 && <div className="text-sm text-stone-500">No assets yet.</div>}
          {items.map((a) => {
            const Icon = ICONS[a.asset_type] || Smartphone;
            return (
              <div key={a.id} className="card-flat">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-md flex items-center justify-center" style={{ background: "var(--color-primary-light)" }}>
                    <Icon className="w-5 h-5" style={{ color: "var(--color-primary)" }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-heading font-semibold">{a.brand} {a.model}</div>
                    <div className="text-xs text-stone-500">{a.asset_type} · {a.imei_serial}</div>
                  </div>
                  <span className="status-pill capitalize" style={{ background: a.status === "assigned" ? "var(--color-primary-light)" : "#F4F1DE", color: a.status === "assigned" ? "var(--color-primary)" : "#57534E" }}>
                    {a.status}
                  </span>
                </div>
                <div className="mt-4 grid grid-cols-2 gap-2 text-xs">
                  <Item label="Assigned To" v={a.assigned_to_name || "Unassigned"} />
                  <Item label="Cost" v={formatINR(a.cost)} />
                  <Item label="Issued" v={a.issue_date || "—"} />
                  <Item label="Warranty" v={a.warranty_until || "—"} />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {show && <AddAssetModal employees={employees} onClose={() => setShow(false)} onCreated={() => { setShow(false); load(); }} />}
    </div>
  );
}

function Item({ label, v }) {
  return <div><div className="kpi-label">{label}</div><div className="text-sm font-medium mt-0.5">{v}</div></div>;
}

function AddAssetModal({ employees, onClose, onCreated }) {
  const [form, setForm] = useState({
    asset_type: "Mobile", brand: "", model: "", imei_serial: "",
    cost: 25000, purchase_date: new Date().toISOString().slice(0, 10),
    warranty_until: "", assigned_to: "",
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    await api.post("/assets", {
      ...form,
      cost: Number(form.cost),
      assigned_to: form.assigned_to || null,
      warranty_until: form.warranty_until || null,
    });
    setBusy(false); onCreated();
  };

  return (
    <div className="fixed inset-0 z-50 bg-stone-900/50 flex items-center justify-center p-4" data-testid="add-asset-modal">
      <div className="bg-white rounded-xl w-full max-w-lg p-6">
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-heading text-xl font-bold">Add Asset</h2>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="grid grid-cols-2 gap-3">
          <Sel label="Asset Type" value={form.asset_type} onChange={(v) => setForm({ ...form, asset_type: v })}>
            {TYPES.map((t) => <option key={t}>{t}</option>)}
          </Sel>
          <In label="Brand" value={form.brand} onChange={(v) => setForm({ ...form, brand: v })} />
          <In label="Model" value={form.model} onChange={(v) => setForm({ ...form, model: v })} />
          <In label="IMEI / Serial" value={form.imei_serial} onChange={(v) => setForm({ ...form, imei_serial: v })} />
          <In label="Cost ₹" type="number" value={form.cost} onChange={(v) => setForm({ ...form, cost: v })} />
          <In label="Purchase Date" type="date" value={form.purchase_date} onChange={(v) => setForm({ ...form, purchase_date: v })} />
          <In label="Warranty Until" type="date" value={form.warranty_until} onChange={(v) => setForm({ ...form, warranty_until: v })} />
          <Sel label="Assign To" value={form.assigned_to} onChange={(v) => setForm({ ...form, assigned_to: v })}>
            <option value="">Unassigned</option>
            {employees.map((e) => <option key={e.id} value={e.id}>{e.full_name}</option>)}
          </Sel>
          <button type="submit" disabled={busy} data-testid="submit-asset-button"
                  className="col-span-2 py-2 mt-2 rounded-md text-white font-medium" style={{ background: "var(--color-primary)" }}>
            {busy ? "Adding…" : "Add Asset"}
          </button>
        </form>
      </div>
    </div>
  );
}

function In({ label, type = "text", value, onChange }) {
  return (
    <label className="block">
      <div className="kpi-label mb-1.5">{label}</div>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)}
             className="w-full px-3 py-2 border border-stone-200 rounded-md text-sm focus:outline-none focus:border-primary" />
    </label>
  );
}
function Sel({ label, value, onChange, children }) {
  return (
    <label className="block">
      <div className="kpi-label mb-1.5">{label}</div>
      <select value={value} onChange={(e) => onChange(e.target.value)}
              className="w-full px-3 py-2 border border-stone-200 rounded-md text-sm bg-white focus:outline-none focus:border-primary">{children}</select>
    </label>
  );
}
